import { SecurityWebapiService } from '../../http/security/security-webapi.service';
import { MatrixKeys, PermissionTypes } from '../constants/security-matrix';
import { Injectable } from '@angular/core';
import { PermissionsObj } from '../models/common-models';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})

/**
 * Service to handle permission checks and updates based on a security matrix.
 * This service uses enums to define permissions and manages visibility and enablement
 * for various fields or actions.
 *
 * The permissions are represented using a combination of the security matrix enums
 * and suffixes `_Visible` and `_Enable` to denote visibility and enablement respectively.
 *
 * - The `MatrixKeys` enum is used to define the keys for permissions.
 * - The `PermissionTypes` enum is used to define the types of permissions (e.g., Visible, Enable).
 * - Permissions are stored in an object (`#permissionsObj`) with default values set to `false`.
 * - The service provides reactive updates to the permissions object using an observable.
 *
 * Example:
 * Intake_Nav_Submission_Reset is a key in MatrixKeys,
 * it's respective permission would be Intake_Nav_Submission_Reset_Visible & Intake_Nav_Submission_Reset_Enable
 * - permissionObj['Intake_Nav_Submission_Reset_Visible'] or permissionObj['Intake_Nav_Submission_Reset_Enable']
 *
 */
export class PermissionCheckService {
  permissionEnums: typeof MatrixKeys = MatrixKeys;
  permissionType: typeof PermissionTypes = PermissionTypes;
  readonly #permissionsObj: PermissionsObj = this.generatePermissionsFromEnum(); // Has permissionkeys for all the modules present in matrixenum
  private permissionObjSubject: BehaviorSubject<PermissionsObj> =
    new BehaviorSubject<PermissionsObj>(this.#permissionsObj);
  permissionsObj$ = this.permissionObjSubject.asObservable();

  constructor(private readonly securityWebapiService: SecurityWebapiService) {}

  // Checks if a specific permission is present for a given field key.
  isPermissionPresent(fieldKey: string, permissionType: string): boolean {
    return this.securityWebapiService.isPermissionPresent(
      fieldKey,
      permissionType
    );
  }

  // Checks if the given permission enum has visibility - visible or enable permission.
  hasVisibilityPermission(permissionEnum: string): boolean {
    return (
      this.isPermissionPresent(permissionEnum, this.permissionType.Visible) ||
      this.isPermissionPresent(permissionEnum, this.permissionType.Enable)
    );
  }

  // Creates permission keys from Matrix Enum
  private generatePermissionsFromEnum(): PermissionsObj {
    const permissions: PermissionsObj = {};

    Object.keys(MatrixKeys).forEach((key) => {
      permissions[`${key}_Visible`] = false;
      permissions[`${key}_Enable`] = false;
    });

    return permissions;
  }

  // Iterate through all permission keys and update their values based on visibility or enablement
  updatePermissions(): void {
    Object.keys(this.#permissionsObj).forEach((key) => {
      const isVisible = key.endsWith('_Visible');
      const isEnable = key.endsWith('_Enable');

      if (isVisible || isEnable) {
        // Determine the base key by removing the '_Visible' or '_Enable' suffix.
        const enumKey = key.replace(isVisible ? '_Visible' : '_Enable', '');
        const enumVal =
          this.permissionEnums[enumKey as keyof typeof MatrixKeys];

        this.#permissionsObj[key] = isVisible
          ? this.hasVisibilityPermission(enumVal)
          : this.isPermissionPresent(enumVal, this.permissionType.Enable);
      }
    });

    this.permissionObjSubject.next(this.#permissionsObj);
  }
}
